import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Form, Button, Message, Checkbox, Radio } from 'semantic-ui-react';
import { useForm, Controller } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { convertObjectKeys, toSnake, toCamel } from '../../utils/change-case';
import { trimObject } from '../../utils/useful_functions';
import additionalMessages from '../../messages';
import { updateAdminToolsApi, getWithAuth } from '../../api';
import {
  getAllowedFeatures,
  getUserLimits,
} from '../mainframe/mainframe.reducer';

// ============================================================================
// Type Definitions
// ============================================================================

type SearchMode = 'email' | 'domain';

interface UserLimits {
  projects: string | number;
  networkSites: string | number;
  subscriberSites: string | number;
  pmpNetworkDevices: string | number;
  pmpLinks: string | number;
  ptpLinks: string | number;
  addressSearch: string | number;
}

interface UserFeatures {
  defaultLidarProjects: boolean;
  defaultLidarLength: string | number;
  allowEmbargoed: boolean;
}

interface BaseInfo {
  limits: UserLimits;
  features: UserFeatures;
}

interface UserInfo extends BaseInfo {
  enabled: boolean;
  emailAddresses?: string[];
}

interface DomainInfo extends BaseInfo {
  enabled: boolean;
  domains?: string[];
}

type FormValues = {
  enabled: boolean;
  limits: UserLimits;
  features: UserFeatures;
  emailAddresses?: string[];
  domains?: string[];
};

interface CheckListItemProps {
  checked: Record<string, boolean>;
  email: string;
  onCheck: (email: string) => void;
  onSelect: (email: string) => void;
}

interface CheckListProps {
  emailAddresses: string[];
  checkedItems: Record<string, boolean>;
  onCheck: (email: string) => void;
  onSelect: (email: string) => void;
}

interface LimitFieldConfig {
  name: keyof UserLimits;
  messageKey: string;
}

// ============================================================================
// Constants
// ============================================================================

const EMPTY_LIMITS: UserLimits = {
  projects: '',
  networkSites: '',
  subscriberSites: '',
  pmpNetworkDevices: '',
  pmpLinks: '',
  ptpLinks: '',
  addressSearch: '',
};

const EMPTY_FEATURES: UserFeatures = {
  defaultLidarProjects: false,
  defaultLidarLength: '',
  allowEmbargoed: false,
};

const LIMIT_FIELDS: LimitFieldConfig[] = [
  { name: 'projects', messageKey: 'adminUserInfoSettingsProjects' },
  { name: 'networkSites', messageKey: 'adminUserInfoSettingsNetworkSites' },
  {
    name: 'subscriberSites',
    messageKey: 'adminUserInfoSettingsSubscriberSites',
  },
  { name: 'ptpLinks', messageKey: 'ptpLinks' },
  {
    name: 'pmpNetworkDevices',
    messageKey: 'adminUserInfoSettingsPMPNetworkDevices',
  },
  { name: 'pmpLinks', messageKey: 'adminUserInfoSettingsPMPLinks' },
];

const ALLOWED_KEYS = new Set([
  'Backspace',
  'Delete',
  'Tab',
  'Escape',
  'Enter',
  'ArrowLeft',
  'ArrowRight',
  'ArrowUp',
  'ArrowDown',
  'Home',
  'End',
]);

// ============================================================================
// Utility Functions
// ============================================================================

const createEmptyUserInfo = (): UserInfo => ({
  enabled: false,
  limits: { ...EMPTY_LIMITS },
  features: { ...EMPTY_FEATURES },
});

const createEmptyDomainInfo = (): DomainInfo => ({
  enabled: true,
  limits: { ...EMPTY_LIMITS },
  features: { ...EMPTY_FEATURES },
});

const isValidNumber = (value: unknown, min: number, max: number): boolean => {
  if (value === '' || value === null || value === undefined) {
    return true;
  }

  const strValue = String(value);
  const numberRegex = /^\d*(\.\d*)?$/;

  if (!numberRegex.test(strValue)) {
    return false;
  }

  const floatValue = parseFloat(strValue);
  return !isNaN(floatValue) && floatValue >= min && floatValue <= max;
};

const handleNumberKeyDown = (e: React.KeyboardEvent): void => {
  // Allow navigation and control keys
  if (ALLOWED_KEYS.has(e.key) || e.ctrlKey || e.metaKey) {
    return;
  }
  // Block if not a digit
  if (!/^\d$/.test(e.key)) {
    e.preventDefault();
  }
};

const handleNumberPaste = (e: React.ClipboardEvent): void => {
  const pastedText = e.clipboardData.getData('text');
  const isNumeric = /^\d*$/.test(pastedText);

  if (!isNumeric) {
    e.preventDefault();
  }
};

const validateNumberField = (value: unknown): boolean => {
  if (!value) {
    return true; // Empty values are allowed
  }

  const num = parseFloat(String(value));

  if (isNaN(num) || num < 0 || !Number.isInteger(num)) {
    return false;
  }

  return isValidNumber(value, 0, Number.MAX_SAFE_INTEGER);
};

// ============================================================================
// Sub-Components
// ============================================================================

const CheckListItem: React.FC<CheckListItemProps> = React.memo(
  ({ checked, email, onCheck, onSelect }) => (
    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '5px' }}>
      <Checkbox checked={checked[email]} onChange={() => onCheck(email)} />
      <a
        style={{ marginLeft: '8px', cursor: 'pointer' }}
        onClick={() => onSelect(email)}
      >
        {email}
      </a>
    </div>
  )
);

CheckListItem.displayName = 'CheckListItem';

const CheckList: React.FC<CheckListProps> = ({
  emailAddresses,
  checkedItems,
  onCheck,
  onSelect,
}) => (
  <div
    style={{
      marginBottom: '10px',
      border: '1px solid #ccc',
      padding: '5px',
      display: 'flex',
      flexDirection: 'column',
      maxHeight: '50vh',
      overflowY: 'auto',
    }}
  >
    {emailAddresses.map((email, idx) => (
      <CheckListItem
        key={`checklist-item-${email}-${idx}`}
        checked={checkedItems}
        email={email}
        onCheck={onCheck}
        onSelect={onSelect}
      />
    ))}
  </div>
);

// ============================================================================
// Main Component
// ============================================================================

function UserInfoSettings() {
  const { formatMessage } = useIntl();
  const dispatch = useDispatch<any>();

  const [userInfo, setUserInfo] = useState<UserInfo | DomainInfo | null>(null);
  const [isUserInfoValModified, setIsUserInfoValModified] = useState(false);
  const [isEmailModified, setIsEmailModified] = useState(false);
  const [checkedItems, setCheckedItems] = useState<Record<string, boolean>>({});
  const [errMsg, setErrMsg] = useState('');
  const [searchMode, setSearchMode] = useState<SearchMode>('email');
  const [searchTerm, setSearchTerm] = useState('');

  const {
    control,
    handleSubmit,
    reset,
    formState: { isValid: formIsValid },
    watch,
  } = useForm<FormValues>({
    mode: 'onChange',
    defaultValues: {
      ...createEmptyUserInfo(),
      emailAddresses: [],
      domains: [],
    },
  });

  const formValues = watch();

  // Computed state for field disabled condition
  const fieldsDisabled = useMemo(
    () => !userInfo || isEmailModified || !!errMsg,
    [userInfo, isEmailModified, errMsg]
  );

  // Sync userInfo to form when it changes
  useEffect(() => {
    if (!userInfo) return;

    const formData: FormValues = {
      enabled: userInfo.enabled,
      limits: userInfo.limits,
      features: userInfo.features,
      emailAddresses:
        'emailAddresses' in userInfo ? userInfo.emailAddresses : undefined,
      domains: 'domains' in userInfo ? userInfo.domains : undefined,
    };

    reset(formData);
  }, [userInfo, reset]);

  // Reset all state when switching search modes
  useEffect(() => {
    reset({
      enabled: true,
      limits: { ...EMPTY_LIMITS },
      features: { ...EMPTY_FEATURES },
      emailAddresses: [],
      domains: [],
    });

    setSearchTerm('');
    setUserInfo(null);
    setErrMsg('');
    setIsEmailModified(false);
    setIsUserInfoValModified(false);
    setCheckedItems({});
  }, [searchMode, reset]);

  const updateUserInfoField = useCallback(
    (name: string, value: unknown, isCheckbox = false) => {
      if (!userInfo) return;

      if (name === 'enabled') {
        setUserInfo({ ...userInfo, enabled: value as boolean });
        setIsUserInfoValModified(true);
        return;
      }

      if (name === 'email') {
        // This is just for tracking that the search term was modified
        setIsEmailModified(true);
        setIsUserInfoValModified(false);
        return;
      }

      // Handle features or limits
      if (name.startsWith('features.')) {
        const featureField = name.replace(
          'features.',
          ''
        ) as keyof UserFeatures;
        const parsedValue = isCheckbox
          ? value
          : parseInt(String(value), 10) || '';

        setUserInfo({
          ...userInfo,
          features: {
            ...userInfo.features,
            [featureField]: parsedValue,
          },
        });
      } else {
        // Handle limit fields
        const parsedValue = parseInt(String(value), 10) || '';

        setUserInfo({
          ...userInfo,
          limits: {
            ...userInfo.limits,
            [name]: parsedValue,
          },
        });
      }

      setIsUserInfoValModified(true);
    },
    [userInfo, searchMode]
  );

  const getSelectedItems = useCallback(() => {
    if (!userInfo) return [];

    if (searchMode === 'domain' && 'domains' in userInfo) {
      const allDomains = formValues.domains || [];
      return allDomains.filter((domain) => checkedItems[domain]);
    }

    if ('emailAddresses' in userInfo) {
      const allEmails = formValues.emailAddresses || [];
      return allEmails.filter((email) => checkedItems[email]);
    }

    return [];
  }, [searchMode, userInfo, formValues, checkedItems]);

  const parseApiResponse = useCallback(
    (res: any): UserInfo | DomainInfo => {
      if (searchMode === 'domain') {
        return {
          enabled: true, // Domain searches always have enabled set to true
          limits: convertObjectKeys(res.limits, toCamel) as UserLimits,
          features: convertObjectKeys(res.features, toCamel) as UserFeatures,
          domains: res.domains || [],
        } as DomainInfo;
      }

      return {
        enabled: res.enabled,
        limits: convertObjectKeys(res.limits, toCamel) as UserLimits,
        features: convertObjectKeys(res.features, toCamel) as UserFeatures,
        emailAddresses: res.email_addresses || [],
      } as UserInfo;
    },
    [searchMode]
  );

  const onFindHandler = useCallback(
    ({ email = null }: { email?: string | null } = {}) => {
      const searchValue = email || searchTerm?.trim();
      if (!searchValue) return;

      const queryParam = searchMode === 'domain' ? 'domain' : 'email';

      getWithAuth(`admin/user?${queryParam}=${searchValue}`)
        .then((res) => {
          const info = parseApiResponse(res);
          setUserInfo(info);

          // Automatically select all results
          const allItems: string[] = [];
          if (searchMode === 'domain' && 'domains' in info) {
            allItems.push(...(info.domains || []));
          } else if ('emailAddresses' in info) {
            allItems.push(...(info.emailAddresses || []));
          }

          const checkedState: Record<string, boolean> = {};
          allItems.forEach((item) => {
            if (item) checkedState[item] = true;
          });
          setCheckedItems(checkedState);

          setErrMsg('');
          setIsEmailModified(false);
          setIsUserInfoValModified(false);
        })
        .catch((err) => {
          setErrMsg(
            searchMode === 'domain' ? 'Unknown domain' : 'Unknown user'
          );

          const emptyInfo =
            searchMode === 'domain'
              ? createEmptyDomainInfo()
              : createEmptyUserInfo();
          setUserInfo(emptyInfo);

          console.error('Unknown user/domain:', err);
        });
    },
    [searchTerm, searchMode, parseApiResponse]
  );

  const handleFormSubmit = useCallback(
    (restoreDefault = false) => {
      if (!userInfo) return;

      // Get selected items or fall back to all items
      const selectedItems = getSelectedItems();
      const hasSelectedItems = selectedItems.length > 0;

      let payload: Record<string, unknown>;

      if (searchMode === 'domain' && 'domains' in userInfo) {
        // DomainLimitsSchema
        payload = {
          domains: hasSelectedItems ? selectedItems : userInfo.domains || [],
          enabled: restoreDefault ? true : userInfo?.enabled,
          limits: restoreDefault
            ? {}
            : convertObjectKeys(userInfo.limits, toSnake),
          features: restoreDefault
            ? {}
            : convertObjectKeys(userInfo.features, toSnake),
        };
      } else if ('emailAddresses' in userInfo) {
        // PatchLimitsSchema
        payload = {
          email_addresses: hasSelectedItems
            ? selectedItems
            : userInfo.emailAddresses || [],
          enabled: restoreDefault ? true : userInfo?.enabled,
          limits: restoreDefault
            ? {}
            : convertObjectKeys(userInfo.limits, toSnake),
          features: restoreDefault
            ? {}
            : convertObjectKeys(userInfo.features, toSnake),
        };
      } else {
        return;
      }

      trimObject(payload);

      updateAdminToolsApi(payload)
        .then(() => {
          dispatch(getAllowedFeatures());
          dispatch(getUserLimits());
          toast(<Message success>Updated user information settings</Message>, {
            autoClose: 1000,
          });
        })
        .catch((err) => {
          setErrMsg(err.message || 'An error occurred');
          console.error('Error while updating admintools', err);
        });
    },
    [userInfo, searchMode, dispatch, getSelectedItems]
  );

  const handleCheckItem = useCallback((item: string) => {
    setCheckedItems((prev) => ({
      ...prev,
      [item]: !prev[item],
    }));
  }, []);

  const handleSelectItem = useCallback(
    (item: string) => {
      setIsEmailModified(true);
      onFindHandler({ email: item });
    },
    [onFindHandler]
  );

  return (
    <>
      <Form
        className="ui form"
        onSubmit={handleSubmit(() => handleFormSubmit(false))}
      >
        <Form.Group>
          <label style={{ marginRight: '20px', fontWeight: 'bold' }}>
            Search by:
          </label>
          <Form.Field>
            <Radio
              label="Email"
              name="searchMode"
              value="email"
              checked={searchMode === 'email'}
              onChange={() => setSearchMode('email')}
            />
          </Form.Field>
          <Form.Field style={{ marginLeft: '20px' }}>
            <Radio
              label="Domain"
              name="searchMode"
              value="domain"
              checked={searchMode === 'domain'}
              onChange={() => setSearchMode('domain')}
            />
          </Form.Field>
        </Form.Group>
        <Form.Group widths="equal">
          <Form.Input
            label={
              searchMode === 'domain'
                ? 'Domain'
                : formatMessage(additionalMessages.adminUserInfoSettingsEmail)
            }
            type="text"
            id="searchTerm"
            name="searchTerm"
            value={searchTerm}
            onChange={(e, { value }) => {
              setSearchTerm(value);
              setIsEmailModified(true);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                onFindHandler({});
              }
            }}
          />
          <Button
            type="button"
            onClick={() => onFindHandler({})}
            style={{ margin: '15px 4px 0' }}
          >
            {formatMessage(additionalMessages.adminUserInfoSettingsFind)}
          </Button>
        </Form.Group>
        {searchMode === 'email' &&
          formValues.emailAddresses &&
          formValues.emailAddresses.length > 0 && (
            <>
              <label htmlFor="user-checklist">Select Users to Update:</label>
              <CheckList
                emailAddresses={formValues.emailAddresses}
                checkedItems={checkedItems}
                onCheck={handleCheckItem}
                onSelect={handleSelectItem}
              />
            </>
          )}
        {searchMode === 'domain' &&
          formValues.domains &&
          formValues.domains.length > 0 && (
            <>
              <label htmlFor="domain-checklist">
                Select Domains to Update:
              </label>
              <CheckList
                emailAddresses={formValues.domains}
                checkedItems={checkedItems}
                onCheck={handleCheckItem}
                onSelect={handleSelectItem}
              />
            </>
          )}
        {searchMode === 'email' && (
          <Form.Group widths="equal">
            <Controller
              name="enabled"
              control={control}
              render={({ field }) => (
                <Form.Checkbox
                  label={formatMessage(
                    additionalMessages.adminUserInfoSettingsEnabled
                  )}
                  id="enabled"
                  name="enabled"
                  checked={field.value}
                  disabled={fieldsDisabled}
                  onChange={(e, { checked }) => {
                    field.onChange(checked);
                    updateUserInfoField('enabled', checked);
                  }}
                />
              )}
            />
          </Form.Group>
        )}
        <Form.Group widths="equal">
          {LIMIT_FIELDS.map(({ name, messageKey }) => (
            <Controller
              key={name}
              name={`limits.${name}`}
              control={control}
              rules={{ validate: validateNumberField }}
              render={({ field }) => (
                <Form.Input
                  label={formatMessage(additionalMessages[messageKey])}
                  type="number"
                  id={name}
                  name={`limits.${name}`}
                  value={field.value}
                  disabled={fieldsDisabled}
                  onChange={(e, { value }) => {
                    field.onChange(value);
                    updateUserInfoField(name, value);
                  }}
                  onKeyDown={handleNumberKeyDown}
                  onPaste={handleNumberPaste}
                />
              )}
            />
          ))}
        </Form.Group>
        <Form.Group widths="equal">
          <Controller
            name="limits.addressSearch"
            control={control}
            rules={{ validate: validateNumberField }}
            render={({ field }) => (
              <Form.Input
                label={formatMessage(additionalMessages.addressSearch)}
                type="number"
                id="addressSearch"
                name="limits.addressSearch"
                value={field.value}
                disabled={fieldsDisabled}
                onChange={(e, { value }) => {
                  field.onChange(value);
                  updateUserInfoField('addressSearch', value);
                }}
                onKeyDown={handleNumberKeyDown}
                onPaste={handleNumberPaste}
              />
            )}
          />
          <Controller
            name="features.defaultLidarLength"
            control={control}
            rules={{ validate: validateNumberField }}
            render={({ field }) => (
              <Form.Input
                label={formatMessage(
                  additionalMessages.adminUserInfoSettingsLidarLength
                )}
                type="number"
                id="lidarLength"
                name="features.defaultLidarLength"
                value={field.value}
                disabled={fieldsDisabled}
                onChange={(e, { value }) => {
                  field.onChange(value);
                  updateUserInfoField('features.defaultLidarLength', value);
                }}
                onKeyDown={handleNumberKeyDown}
                onPaste={handleNumberPaste}
              />
            )}
          />
          <Controller
            name="features.defaultLidarProjects"
            control={control}
            render={({ field }) => (
              <Form.Checkbox
                label={formatMessage(
                  additionalMessages.adminUserInfoSettingsLidarProjects
                )}
                style={{ paddingTop: '1.5em' }}
                id="lidarProjects"
                name="features.defaultLidarProjects"
                checked={field.value}
                disabled={fieldsDisabled}
                onChange={(e, { checked }) => {
                  field.onChange(checked);
                  updateUserInfoField(
                    'features.defaultLidarProjects',
                    checked,
                    true
                  );
                }}
              />
            )}
          />
          <Controller
            name="features.allowEmbargoed"
            control={control}
            render={({ field }) => (
              <Form.Checkbox
                label={formatMessage(
                  additionalMessages.adminUserInfoSettingsAllowEmbargoed
                )}
                style={{ paddingTop: '1.5em' }}
                id="allowEmbargoed"
                name="features.allowEmbargoed"
                checked={field.value}
                disabled={fieldsDisabled}
                onChange={(e, { checked }) => {
                  field.onChange(checked);
                  updateUserInfoField('features.allowEmbargoed', checked, true);
                }}
              />
            )}
          />
        </Form.Group>
        <Form.Group style={{ justifyContent: 'end', marginBottom: '0px' }}>
          <Button
            type="button"
            disabled={!userInfo || isEmailModified}
            color="blue"
            onClick={() => handleFormSubmit(true)}
            aria-label="Restore default settings"
          >
            {formatMessage(
              additionalMessages.adminUserInfoSettingsRestoreDefault
            )}
          </Button>
          <Button
            type="submit"
            disabled={
              !userInfo ||
              !isUserInfoValModified ||
              isEmailModified ||
              !formIsValid
            }
            color="blue"
            aria-label="Update user information"
          >
            {formatMessage(additionalMessages.adminUserInfoSettingsUpdate)}
          </Button>
        </Form.Group>
      </Form>
      {errMsg && (
        <Message negative attached>
          <Message.Header>
            {formatMessage(additionalMessages.adminToolsError)}
          </Message.Header>
          <p>{errMsg}</p>
        </Message>
      )}
    </>
  );
}

export default UserInfoSettings;
